/**
 * Phase 4A — Customer CRM admin workspace.
 *
 * ONE file, list + detail + add-modal via query params:
 *   /admin/customers                → list
 *   /admin/customers?id=<pid>       → detail (with edit)
 *
 * Reuses:
 *   GET   /api/admin/customers/list     — Phase 4A CRM list
 *   GET   /api/admin/customers/{id}     — Phase 1B master fetch
 *   GET   /api/admin/customers/{id}/timeline — Phase 1B timeline
 *   POST  /api/admin/customers          — Phase 4A create
 *   PATCH /api/admin/customers/{id}     — Phase 4A edit
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, User, X, AlertTriangle, Link2, CheckCircle2 } from "lucide-react";
import { BootstrapControls } from "./BootstrapControls";
import { AddWanted } from "./AdminWantedItems";

function money(n) { return `$${(Number(n) || 0).toFixed(2)}`; }
function useQ() { const p = new URLSearchParams(window.location.search); return { id: p.get("id") }; }
function nav(qs) { window.history.pushState(null, "", `/admin/customers${qs}`); window.dispatchEvent(new Event("popstate")); }

export default function AdminCustomers() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const on = () => setTick((t) => t + 1);
    window.addEventListener("popstate", on);
    return () => window.removeEventListener("popstate", on);
  }, []);
  const q = useQ(); void tick;
  if (q.id) return <Detail id={q.id}/>;
  return <List/>;
}

function List() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  useEffect(() => {
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/customers/list",
                                    { params: { q: q || undefined, limit: 100 } });
        if (alive) setRows(r.data?.results || []);
      } catch { if (alive) setRows([]); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-24"
         data-testid="customers-list">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-3 flex items-center gap-2">
        <a href="/admin" className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Admin · CRM</div>
          <div className="font-bold">Customers</div>
        </div>
        <BootstrapControls/>
        <button onClick={() => setShowAdd(true)}
                data-testid="customers-add-btn"
                className="bg-[var(--panini-red,_#dc2626)] text-white text-sm font-bold rounded-2xl px-3 py-2 flex items-center gap-1">
          <Plus size={14}/> Add
        </button>
      </div>
      <div className="p-3 space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" size={16}/>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search name · email · phone · customer ID"
                 className="w-full border-2 border-[var(--ink)] rounded-2xl pl-9 pr-3 py-3 text-sm"
                 data-testid="customers-search-input"/>
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-[var(--smoke)] p-3 border-2 border-dashed rounded-xl"
               data-testid="customers-empty">
            {q ? `No customers match "${q}".` : "No customers yet — tap Add to create one."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="customers-rows">
            {rows.map((c) => (
              <a key={c.id} href={`/admin/customers?id=${c.id}`}
                 className="block border-2 border-[var(--ink)] rounded-2xl p-3 bg-white active:bg-red-50"
                 data-testid={`customer-row-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">
                      {c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—"}
                    </div>
                    <div className="text-[11px] text-[var(--smoke)] truncate">
                      {c.email || c.phone || c.public_customer_id}
                    </div>
                  </div>
                  <div className="text-[10px] text-[var(--smoke)] uppercase">
                    {c.public_customer_id}
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
      {showAdd && (
        <AddModal onClose={() => setShowAdd(false)}
                    onCreated={(c) => { setShowAdd(false); nav(`?id=${c.id}`); }}/>
      )}
    </div>
  );
}


function AddModal({ onClose, onCreated }) {
  const [f, setF] = useState({ first_name: "", last_name: "",
                                    email: "", phone: "", notes: "" });
  const [dupe, setDupe] = useState(null);   // {status, customer, matched_by} | null
  const [conflict, setConflict] = useState(null); // {conflict_candidates, reasons} | null
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true); setDupe(null); setConflict(null);
    try {
      const r = await api.post("/admin/customers", f);
      const st = r.data?.status;
      if (st === "created") {
        toast.success(`Customer ${r.data.customer.public_customer_id} created.`);
        onCreated(r.data.customer);
      } else if (st === "duplicate") {
        setDupe(r.data);
      } else if (st === "conflict") {
        setConflict(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  }

  const canSave = f.email.trim() || f.phone.trim() || f.first_name.trim() || f.last_name.trim();

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end sm:items-center justify-center"
         data-testid="customers-add-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div className="font-bold">+ Add Customer</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="text-[11px] text-[var(--smoke)]">
          At least one of email, phone, or name is required.
          Creating a CRM customer does NOT create a CollectorUp login.
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="First name" value={f.first_name}
                 onChange={(e) => setF({ ...f, first_name: e.target.value })}
                 className="border rounded-xl px-3 py-2 text-sm"
                 data-testid="customer-add-first"/>
          <input placeholder="Last name" value={f.last_name}
                 onChange={(e) => setF({ ...f, last_name: e.target.value })}
                 className="border rounded-xl px-3 py-2 text-sm"
                 data-testid="customer-add-last"/>
        </div>
        <input placeholder="Email" value={f.email}
               onChange={(e) => setF({ ...f, email: e.target.value })}
               className="w-full border rounded-xl px-3 py-2 text-sm"
               data-testid="customer-add-email"/>
        <input placeholder="Phone" value={f.phone}
               onChange={(e) => setF({ ...f, phone: e.target.value })}
               className="w-full border rounded-xl px-3 py-2 text-sm"
               data-testid="customer-add-phone"/>
        <textarea rows="2" placeholder="Optional notes" value={f.notes}
                    onChange={(e) => setF({ ...f, notes: e.target.value })}
                    className="w-full border rounded-xl px-3 py-2 text-sm"
                    data-testid="customer-add-notes"/>
        {dupe && (
          <div className="border-2 border-yellow-400 bg-yellow-50 rounded-xl p-2 text-xs flex items-start gap-2"
               data-testid="customer-add-duplicate">
            <AlertTriangle size={14} className="text-yellow-700 mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold text-yellow-900">
                Existing customer matched {dupe.matched_by?.join(", ")}.
              </div>
              <a href={`/admin/customers?id=${dupe.customer.id}`}
                 className="text-yellow-800 underline">
                Open {dupe.customer.display_name || dupe.customer.public_customer_id}
              </a>
            </div>
          </div>
        )}
        {conflict && (
          <div className="border-2 border-red-500 bg-red-50 rounded-xl p-2 text-xs"
               data-testid="customer-add-conflict">
            <div className="font-semibold text-red-800 flex items-center gap-1">
              <AlertTriangle size={14}/> Identity conflict — will not merge.
            </div>
            <div className="text-red-700 mt-1">
              {conflict.conflict_reasons?.join(" · ")}
            </div>
          </div>
        )}
        <button onClick={save} disabled={!canSave || saving}
                data-testid="customer-add-save-btn"
                className={`w-full rounded-2xl py-3 font-bold text-white ${
                  !canSave || saving ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                }`}>
          {saving ? "Saving…" : "Save Customer"}
        </button>
      </div>
    </div>
  );
}

function CustomerWantedItems({ customerId }) {
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  async function load() {
    try {
      const r = await api.get("/admin/wanted-items",
                                  { params: { customer_id: customerId, limit: 50 } });
      setRows(r.data.wanted_items || []);
    } catch { setRows([]); }
  }
  useEffect(() => { load(); }, [customerId]);
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-1"
              data-testid="customer-wanted-items">
      <div className="flex justify-between items-center">
        <div className="text-[10px] uppercase text-[var(--smoke)]">Wanted Items</div>
        <button onClick={() => setShowAdd(true)}
                data-testid="customer-add-wanted"
                className="text-xs font-semibold text-[var(--panini-red)]">
          + Add Wanted
        </button>
      </div>
      {rows == null && <div className="text-xs text-[var(--smoke)]">Loading…</div>}
      {rows && rows.length === 0 && <div className="text-xs text-[var(--smoke)]">No wanted items.</div>}
      {rows && rows.map((w) => (
        <div key={w.id} className="text-xs border-t pt-1 first:border-0 first:pt-0">
          <b>
            {w.item_type === "sticker" && `${w.sticker_code} · ${w.variant?.toUpperCase()}`}
            {w.item_type === "shopify_product" && (w.product_title || w.shopify_variant_id)}
            {w.item_type === "custom" && w.description}
          </b>
          <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${
            w.status === "wanted" ? "bg-yellow-200"
              : w.status === "found" ? "bg-blue-200"
              : w.status === "fulfilled" ? "bg-green-200"
              : "bg-gray-200"
          }`}>{w.status}</span>
          <span className="text-[var(--smoke)]"> · qty {w.quantity}
            {w.target_price != null && ` · $${w.target_price}`}
          </span>
        </div>
      ))}
      {showAdd && <AddWanted defaultCustomerId={customerId}
                              onClose={() => setShowAdd(false)}
                              onCreated={() => { setShowAdd(false); load(); }}/>}
    </section>
  );
}




function CollectorAccountSection({ cust, onLinked }) {
  const [showLink, setShowLink] = useState(false);
  const linked = cust.linked_collector;
  return (
    <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-1"
                data-testid="customer-collector-account">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase text-[var(--smoke)]">CollectorUp Account</div>
        {!cust.collector_id && (
          <button onClick={() => setShowLink(true)}
                  data-testid="customer-link-collector-btn"
                  className="text-xs font-semibold text-[var(--panini-red)] flex items-center gap-1">
            <Link2 size={12}/> Link Collector Account
          </button>
        )}
      </div>
      {cust.collector_id && linked ? (
        <div className="space-y-1 text-sm" data-testid="customer-linked-collector">
          <div className="flex items-center gap-1 text-green-700 font-semibold">
            <CheckCircle2 size={14}/> Linked
          </div>
          <div><b>Collector ID:</b> {linked.public_collector_id || cust.collector_id}</div>
          {linked.display_name && <div><b>Display:</b> {linked.display_name}</div>}
          {linked.email && <div><b>Email:</b> {linked.email}</div>}
          {linked.phone && <div><b>Phone:</b> {linked.phone}</div>}
          {linked.created_at && <div className="text-[11px] text-[var(--smoke)]">
            Joined: {new Date(linked.created_at).toLocaleDateString()}
          </div>}
          {cust.collector_link_method && (
            <div className="text-[11px] text-[var(--smoke)]">
              Link method: {cust.collector_link_method}
            </div>
          )}
        </div>
      ) : cust.collector_id && !linked ? (
        <div className="border-2 border-yellow-400 bg-yellow-50 rounded-xl p-2 text-xs"
             data-testid="customer-link-broken">
          <div className="font-semibold text-yellow-900">Account match requires review</div>
          <div className="text-yellow-800">Linked collector_id {cust.collector_id} not found.</div>
        </div>
      ) : (
        <div className="text-sm">Not linked</div>
      )}
      {showLink && (
        <LinkCollectorModal customerId={cust.id}
                             onClose={() => setShowLink(false)}
                             onLinked={() => { setShowLink(false); onLinked(); }}/>
      )}
    </section>
  );
}


function LinkCollectorModal({ customerId, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  async function search() {
    if (!q.trim()) return;
    setSearching(true); setCandidate(null); setPreflight(null);
    try {
      const r = await api.get(`/admin/customers/${customerId}/link-search-candidate`,
                                  { params: { q } });
      setCandidate(r.data?.collector || null);
      setPreflight(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed.");
    } finally { setSearching(false); }
  }

  async function confirmLink() {
    if (!candidate?.id) return;
    setLinking(true);
    try {
      const r = await api.post(`/admin/customers/${customerId}/link-collector`,
                                  { collector_id: candidate.id });
      const st = r.data?.status;
      if (st === "linked" || st === "existing") {
        toast.success(st === "linked" ? "Collector linked." : "Already linked.");
        onLinked();
      } else if (st === "conflict") {
        setPreflight(r.data);
      } else {
        toast.error("Link failed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed.");
    } finally { setLinking(false); }
  }

  const status = preflight?.status;
  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-end sm:items-center justify-center"
         data-testid="link-collector-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-md p-4 space-y-3 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <div className="font-bold">Link Collector Account</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="text-[11px] text-[var(--smoke)]">
          Search by exact email, phone, or Collector ID. Never fuzzy.
        </div>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="email · phone · CU-…"
                 className="flex-1 border rounded-xl px-3 py-2 text-sm"
                 data-testid="link-collector-search-input"
                 onKeyDown={(e) => e.key === "Enter" && search()}/>
          <button onClick={search} disabled={searching || !q.trim()}
                  data-testid="link-collector-search-btn"
                  className="bg-[var(--ink)] text-white rounded-xl px-3 text-sm font-semibold">
            {searching ? "…" : "Search"}
          </button>
        </div>
        {preflight && status === "no_match" && (
          <div className="text-sm text-[var(--smoke)]" data-testid="link-no-match">
            No exact match for that identifier.
          </div>
        )}
        {candidate && (
          <div className="border-2 border-[var(--ink)] rounded-xl p-3 text-sm space-y-1"
               data-testid="link-candidate">
            <div><b>{candidate.display_name || candidate.public_collector_id}</b></div>
            <div className="text-[11px] text-[var(--smoke)]">
              {candidate.public_collector_id} · {candidate.email || "no email"} · {candidate.phone || "no phone"}
            </div>
            {status === "conflict" && (
              <div className="border-2 border-red-500 bg-red-50 rounded-xl p-2 text-xs mt-1"
                   data-testid="link-conflict">
                <div className="font-semibold text-red-800 flex items-center gap-1">
                  <AlertTriangle size={14}/> Cannot link — conflict.
                </div>
                <div className="text-red-700">{(preflight.conflict_reasons || []).join(" · ")}</div>
              </div>
            )}
            {status === "existing" && (
              <div className="text-green-700 text-xs font-semibold">Already linked.</div>
            )}
            {status === "safe" && (
              <button onClick={confirmLink} disabled={linking}
                      data-testid="link-confirm-btn"
                      className={`w-full rounded-2xl py-2 mt-2 font-bold text-white text-sm ${
                        linking ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                      }`}>
                {linking ? "Linking…" : "Confirm Link"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function Detail({ id }) {
  const [cust, setCust] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({});
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);

  async function reload() {
    try {
      const [c, t] = await Promise.all([
        api.get(`/admin/customers/${id}`),
        api.get(`/admin/customers/${id}/timeline`),
      ]);
      setCust(c.data);
      setTimeline(t.data);
      setF({ first_name: c.data.first_name || "",
              last_name:  c.data.last_name  || "",
              email:      c.data.email      || "",
              phone:      c.data.phone      || "",
              notes:      c.data.notes      || "" });
    } catch { setCust(null); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  async function save() {
    setSaving(true); setConflict(null);
    try {
      const r = await api.patch(`/admin/customers/${id}`, f);
      if (r.data?.status === "conflict") {
        setConflict(r.data);
      } else {
        toast.success("Saved.");
        setEditing(false);
        await reload();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  }

  if (!cust) return <div className="p-6 text-sm text-[var(--smoke)]">Loading…</div>;

  const sticker = timeline?.sticker_orders || [];
  const salesOrders = sticker.filter((o) => o.order_type === "sales_order");
  const normalSales = sticker.filter((o) => o.order_type !== "sales_order");
  const certs = timeline?.certificate_orders || [];

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)] pb-24"
         data-testid="customer-detail">
      <div className="sticky top-0 z-10 bg-white border-b-2 border-[var(--ink)] p-3 flex items-center gap-2">
        <a href="/admin/customers" className="p-1"><ArrowLeft size={20}/></a>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Customer</div>
          <div className="font-bold truncate">{cust.display_name || cust.public_customer_id}</div>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)}
                  data-testid="customer-edit-btn"
                  className="text-sm font-semibold text-[var(--panini-red)]">
            Edit
          </button>
        )}
      </div>
      <div className="p-3 space-y-3">
        <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white space-y-2"
                    data-testid="customer-detail-identity">
          <div className="text-[10px] uppercase text-[var(--smoke)]">Identity</div>
          {!editing ? (
            <>
              <div className="text-sm"><b>Name:</b> {cust.display_name || "—"}</div>
              <div className="text-sm"><b>Email:</b> {cust.email || "—"}</div>
              <div className="text-sm"><b>Phone:</b> {cust.phone || "—"}</div>
              <div className="text-sm"><b>Public ID:</b>
                <span data-testid="customer-public-id"> {cust.public_customer_id}</span>
              </div>
              {cust.notes && <div className="text-xs text-[var(--smoke)] pt-1 border-t">
                {cust.notes}
              </div>}
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })}
                       placeholder="First name" className="border rounded-xl px-3 py-2 text-sm"/>
                <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })}
                       placeholder="Last name" className="border rounded-xl px-3 py-2 text-sm"/>
              </div>
              <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                     placeholder="Email" className="w-full border rounded-xl px-3 py-2 text-sm"
                     data-testid="customer-edit-email"/>
              <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                     placeholder="Phone" className="w-full border rounded-xl px-3 py-2 text-sm"/>
              <textarea rows="3" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}
                          placeholder="Notes" className="w-full border rounded-xl px-3 py-2 text-sm"/>
              {conflict && (
                <div className="border-2 border-red-500 bg-red-50 rounded-xl p-2 text-xs"
                     data-testid="customer-edit-conflict">
                  <div className="font-semibold text-red-800 flex items-center gap-1">
                    <AlertTriangle size={14}/> Identity conflict — will not merge.
                  </div>
                  <div className="text-red-700 mt-1">
                    {conflict.conflict_reasons?.join(" · ")}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => { setEditing(false); setConflict(null); reload(); }}
                        className="border rounded-xl py-2 text-sm font-semibold">
                  Cancel
                </button>
                <button onClick={save} disabled={saving}
                        data-testid="customer-save-edit-btn"
                        className={`rounded-xl py-2 text-sm font-bold text-white ${
                          saving ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                        }`}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </>
          )}
        </section>

        <CollectorAccountSection cust={cust} onLinked={reload}/>
        <CustomerWantedItems customerId={cust.id}/>

        {salesOrders.length > 0 && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
                     data-testid="customer-sales-orders">
            <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Sales Orders</div>
            <div className="divide-y">
              {salesOrders.map((o) => (
                <a key={o.public_id} href={`/admin/sales-orders?id=${o.public_id}`}
                   className="py-2 flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{o.public_id}</div>
                    <div className="text-[10px] uppercase text-[var(--smoke)]">
                      {o.payment_status || o.status} · {o.fulfillment_status || "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">{money(o.pricing_snapshot?.totals?.total)}</div>
                    {(o.balance_due || 0) > 0 && (
                      <div className="text-[11px] text-red-600 font-semibold">
                        Balance {money(o.balance_due)}
                      </div>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}

        {normalSales.length > 0 && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
                     data-testid="customer-sales">
            <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Sticker Orders / Sales</div>
            <div className="divide-y">
              {normalSales.slice(0, 20).map((o) => (
                <div key={o.public_id} className="py-2 flex items-center justify-between text-sm">
                  <div className="min-w-0 truncate">{o.public_id}</div>
                  <div>{money(o.pricing_snapshot?.totals?.total)}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {certs.length > 0 && (
          <section className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white"
                     data-testid="customer-certs">
            <div className="text-[10px] uppercase text-[var(--smoke)] mb-1">Certificates</div>
            <div className="divide-y">
              {certs.slice(0, 20).map((c) => (
                <div key={c.public_id} className="py-2 flex items-center justify-between text-sm">
                  <div className="min-w-0 truncate">
                    {c.public_order_number || c.public_id}
                  </div>
                  <div className="text-[10px] uppercase text-[var(--smoke)]">
                    {c.payment_status} · {c.fulfillment_status}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
